import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateCampaignRequest, GetAllCampaignsResponse, GetCampaignResponse, UpdateCampaignImageRequest, UpdateCampaignRequest } from '@/types/campaign';
import { CampaignManagementService } from '@/services/campaign/campaignManagementService';

export const useCampaignManagement = (
  page: number = 1,
  limit: number = 10,
  campaignId?: string,
  listingsPage: number = 1,
  listingsLimit: number = 10,
  listingStatuses?: string[]
) => {
  const queryClient = useQueryClient();

  // Read campaigns via management service (same backend in prod)
  const getCampaignsQuery = useQuery<GetAllCampaignsResponse>({
    queryKey: ['campaigns', page, limit],
    queryFn: () => CampaignManagementService.getAllCampaigns(page, limit),
    // staleTime: 5 * 60 * 1000,
    // gcTime: 10 * 60 * 1000,
  });

  const getCampaignQuery = useQuery<GetCampaignResponse>({
    queryKey: ['campaign', campaignId, listingsPage, listingsLimit, listingStatuses],
    queryFn: () =>
      CampaignManagementService.getCampaign(campaignId!, listingsPage, listingsLimit, listingStatuses),
    enabled: !!campaignId,
    // staleTime: 5 * 60 * 1000,
    // gcTime: 10 * 60 * 1000,
  });


  // Create campaign mutation
  const createCampaignMutation = useMutation({
    mutationFn: (request: CreateCampaignRequest) => 
      CampaignManagementService.createCampaign(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  // Update campaign mutation
  const updateCampaignMutation = useMutation({
    mutationFn: (request: UpdateCampaignRequest) => 
      CampaignManagementService.updateCampaign(request),
    onSuccess: () => {
      // Invalidate all campaign-related queries to ensure UI updates everywhere
      queryClient.invalidateQueries({ queryKey: ['campaigns'] }); // For /auctions page
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] }); // For specific campaign details
      queryClient.invalidateQueries({ queryKey: ['publicAuctionCampaigns'] }); // For landing page (AuctionGroups)
    },
  });

  // Update campaign image mutation
  const updateCampaignImageMutation = useMutation({
    mutationFn: ({ campaignId, request }: { campaignId: string; request: UpdateCampaignImageRequest }) => 
      CampaignManagementService.updateCampaignImage(campaignId, request),
    onSuccess: (fileObject, variables) => {
      // Invalidate all campaign queries to refresh the list with new image
      queryClient.invalidateQueries({ queryKey: ['campaigns'] }); // For /auctions page
      queryClient.invalidateQueries({ queryKey: ['campaign', variables.campaignId] }); // For specific campaign details
      queryClient.invalidateQueries({ queryKey: ['publicAuctionCampaigns'] }); // For landing page (AuctionGroups)
    },
  });

  // Remove campaign image mutation
  const removeCampaignImageMutation = useMutation({
    mutationFn: ({ campaignId, mediaId }: { campaignId: string; mediaId: string }) => 
      CampaignManagementService.removeCampaignImage(campaignId, mediaId),
    onSuccess: (_, variables) => {
      // Patch all cached campaign lists to remove image
      const matchingLists = queryClient.getQueriesData<GetAllCampaignsResponse>({ queryKey: ['campaigns'] });
      matchingLists.forEach(([key, data]) => {
        if (!data) return;
        const patched = {
          ...data,
          campaigns: data.campaigns.map((c) =>
            c._id === variables.campaignId ? { ...c, thumbnailImage: undefined } : c
          ),
        };
        queryClient.setQueryData(key, patched);
      });
      // Also refresh specific campaign details and public campaigns
      queryClient.invalidateQueries({ queryKey: ['campaign', variables.campaignId] });
      queryClient.invalidateQueries({ queryKey: ['publicAuctionCampaigns'] }); // For landing page (AuctionGroups)
    },
  });


  // Delete campaign mutation
  const deleteCampaignMutation = useMutation({
    mutationFn: (campaignId: string) => 
      CampaignManagementService.deleteCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  // Cancel campaign mutation
  const cancelCampaignMutation = useMutation({
    mutationFn: (campaignId: string) => 
      CampaignManagementService.cancelCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
      queryClient.invalidateQueries({ queryKey: ['publicAuctionCampaigns'] });
    },
  });

  // Activate campaign mutation
  const activateCampaignMutation = useMutation({
    mutationFn: (campaignId: string) => 
      CampaignManagementService.activateCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
      queryClient.invalidateQueries({ queryKey: ['publicAuctionCampaigns'] });
    },
  });

  // Add listing to campaign mutation
  const addListingToCampaignMutation = useMutation({
    mutationFn: ({ campaignId, listingId }: { campaignId: string; listingId: string }) => 
      CampaignManagementService.addListingToCampaign(campaignId, listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
  });

  // Remove listing from campaign mutation
  const removeListingFromCampaignMutation = useMutation({
    mutationFn: ({ campaignId, listingId }: { campaignId: string; listingId: string }) => 
      CampaignManagementService.removeListingFromCampaign(campaignId, listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
  });

  return {
    // Queries
    campaigns: getCampaignsQuery.data?.campaigns || [],
    totalCampaigns: getCampaignsQuery.data?.total || 0,
    isLoadingCampaigns: getCampaignsQuery.isLoading,
    campaignsError: getCampaignsQuery.error,
    refetchCampaigns: getCampaignsQuery.refetch,

    campaign: getCampaignQuery.data?.campaign,
    listings: getCampaignQuery.data?.listings || [],
    totalListings: getCampaignQuery.data?.totalListings || 0,
    isLoadingCampaign: getCampaignQuery.isLoading,
    campaignError: getCampaignQuery.error,
    refetchCampaign: getCampaignQuery.refetch,
    
    // Mutations
    createCampaignAsync: createCampaignMutation.mutateAsync,
    updateCampaign: updateCampaignMutation.mutateAsync,
    updateCampaignImage: updateCampaignImageMutation.mutateAsync,
    removeCampaignImage: removeCampaignImageMutation.mutateAsync,
    deleteCampaign: deleteCampaignMutation.mutateAsync,
    cancelCampaign: cancelCampaignMutation.mutateAsync,
    activateCampaign: activateCampaignMutation.mutateAsync,
    addListingToCampaign: addListingToCampaignMutation.mutateAsync,
    removeListingFromCampaign: removeListingFromCampaignMutation.mutateAsync,
    
    // Mutation states
    isCreating: createCampaignMutation.isPending,
    isUpdating: updateCampaignMutation.isPending,
    isUpdatingImage: updateCampaignImageMutation.isPending,
    isRemovingImage: removeCampaignImageMutation.isPending,
    isDeleting: deleteCampaignMutation.isPending,
    isCancelling: cancelCampaignMutation.isPending,
    isActivating: activateCampaignMutation.isPending,
    isAddingListing: addListingToCampaignMutation.isPending,
    isRemovingListing: removeListingFromCampaignMutation.isPending,
    
    // Mutation errors
    createError: createCampaignMutation.error?.message || null,
    updateError: updateCampaignMutation.error?.message || null,
    updateImageError: updateCampaignImageMutation.error?.message || null,
    deleteError: deleteCampaignMutation.error?.message || null,
    addListingError: addListingToCampaignMutation.error?.message || null,
    removeListingError: removeListingFromCampaignMutation.error?.message || null,
    
    // Mutation success states
    createSuccess: createCampaignMutation.isSuccess,
    updateSuccess: updateCampaignMutation.isSuccess,
    updateImageSuccess: updateCampaignImageMutation.isSuccess,
    deleteSuccess: deleteCampaignMutation.isSuccess,
    addListingSuccess: addListingToCampaignMutation.isSuccess,
    removeListingSuccess: removeListingFromCampaignMutation.isSuccess,
    
    // Reset functions
    resetCreate: createCampaignMutation.reset,
    resetUpdate: updateCampaignMutation.reset,
    resetUpdateImage: updateCampaignImageMutation.reset,
    resetDelete: deleteCampaignMutation.reset,
    resetAddListing: addListingToCampaignMutation.reset,
    resetRemoveListing: removeListingFromCampaignMutation.reset,
  };
};


